import { z } from 'zod';

/**
 * Normalized position produced by the device gateway (OBD) or the driver-app
 * batch endpoint (PHONE), and consumed by the ingest worker. This is the single
 * contract between the three processes.
 */
export const PositionIngestSchema = z.object({
  tenantId: z.string().uuid(),
  vehicleId: z.string().uuid(),
  deviceId: z.string().uuid().nullable().default(null),
  driverId: z.string().uuid().nullable().default(null),
  source: z.enum(['OBD', 'PHONE']),
  /** ISO-8601, device/phone GPS time (not receive time). */
  recordedAt: z.string().datetime({ offset: true }),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  speedKph: z.number().min(0).max(400).nullable().default(null),
  heading: z.number().int().min(0).max(360).nullable().default(null),
  altitudeM: z.number().nullable().default(null),
  satellites: z.number().int().min(0).nullable().default(null),
  hdop: z.number().min(0).nullable().default(null),
  /** Phone only: horizontal accuracy in meters. */
  accuracyM: z.number().min(0).nullable().default(null),
  ignition: z.boolean().nullable().default(null),
  motion: z.boolean().nullable().default(null),
  odometerKm: z.number().min(0).nullable().default(null),
  fuelLevelPct: z.number().min(0).max(100).nullable().default(null),
  extVoltageMv: z.number().int().nullable().default(null),
  batteryVoltageMv: z.number().int().nullable().default(null),
  rpm: z.number().int().min(0).nullable().default(null),
  coolantTempC: z.number().int().nullable().default(null),
  gsmSignal: z.number().int().min(0).max(5).nullable().default(null),
  /** Teltonika: AVL IO id that triggered the record. */
  eventIo: z.number().int().nullable().default(null),
  events: z
    .array(z.object({ type: z.string(), value: z.number().optional() }))
    .default([]),
  raw: z.record(z.unknown()).optional(),
  /** Phone: client-generated idempotency key. */
  clientId: z.string().max(64).optional(),
});
export type PositionIngest = z.infer<typeof PositionIngestSchema>;

/** Job payload on the BullMQ `positions` queue: one device/phone batch, time-ordered. */
export const PositionsJobSchema = z.object({
  batch: z.array(PositionIngestSchema).min(1).max(1000),
});
export type PositionsJob = z.infer<typeof PositionsJobSchema>;

/** Driver app → POST /driver/positions/batch */
export const PhonePositionSchema = z.object({
  clientId: z.string().min(8).max(64),
  recordedAt: z.string().datetime({ offset: true }),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  accuracyM: z.number().min(0),
  speedKph: z.number().min(0).max(400).optional(),
  heading: z.number().int().min(0).max(360).optional(),
  altitudeM: z.number().optional(),
  isMoving: z.boolean().optional(),
  battery: z.number().min(0).max(100).optional(),
  provider: z.enum(['gps', 'network', 'fused']).optional(),
});
export type PhonePosition = z.infer<typeof PhonePositionSchema>;

export const PhonePositionsBatchSchema = z.object({
  appInstanceId: z.string().min(8).max(128),
  batch: z.array(PhonePositionSchema).min(1).max(500),
});
export type PhonePositionsBatch = z.infer<typeof PhonePositionsBatchSchema>;

export const PhonePositionsConfigSchema = z.object({
  intervalMovingSec: z.number().int().min(5).default(15),
  intervalStoppedSec: z.number().int().min(30).default(120),
  minDistanceM: z.number().int().min(0).default(20),
  batchMaxAgeSec: z.number().int().min(10).default(30),
});
export type PhonePositionsConfig = z.infer<typeof PhonePositionsConfigSchema>;

export const PhonePositionsBatchResponseSchema = z.object({
  accepted: z.number().int(),
  rejected: z.array(z.object({ clientId: z.string(), reason: z.string() })),
  serverTime: z.string().datetime({ offset: true }),
  config: PhonePositionsConfigSchema,
});
export type PhonePositionsBatchResponse = z.infer<typeof PhonePositionsBatchResponseSchema>;
